import type React from "react";
import type { CSSProperties } from "react";
import {
  DashIcon,
  CasesIcon,
  RulesIcon,
  DocIcon,
  UploadIcon,
  ChecklistIcon,
  QueueIcon,
  QuestIcon,
  AssessIcon,
  SubsIcon,
  TeamIcon,
  AccountManagerIcon,
  AuditIcon,
  CostIcon,
  CogIcon,
  LabelIcon,
  BugIcon,
  AMDashIcon,
  AMCustomersIcon,
  AMComplianceIcon,
  AMAssessmentsIcon,
  AMReportsIcon,
  AMSettingsIcon,
} from "./ShellIcons";

export type ToneType = "ok" | "warn" | "risk" | "info";

export interface SubNavItem {
  id: string;
  label: string;
  path: string;
  tone?: ToneType;
  /** Optional right-aligned numeric badge (curated view counts) */
  count?: number;
  /** Optional leading icon (used by Pinned saved-views) */
  icon?: React.FC<{ size?: number; style?: CSSProperties }>;
}

export interface RailNavItem {
  id: string;
  label: string;
  icon: React.FC<{ size?: number; style?: React.CSSProperties }>;
  group: string;
  /** Direct path for leaf nodes (no sub-nav) */
  path?: string;
  /** Sub-nav items */
  sub?: SubNavItem[];
  /** Pinned saved-views shown beneath the curated sub-nav list */
  pinned?: SubNavItem[];
  /** Dev-only item */
  devOnly?: boolean;
}

// Groups for visual separators in the rail
export const RAIL_GROUPS = [
  "Overview",
  "Compliance",
  "Library",
  "Assessment",
  "Admin",
  "Trust",
  "AM",
] as const;

export const RAIL_NAV: RailNavItem[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: DashIcon,
    group: "Overview",
    path: "/",
  },
  {
    id: "cases",
    label: "Cases",
    icon: CasesIcon,
    group: "Compliance",
    // Counts are intentionally omitted until a live count endpoint exists —
    // hardcoded numbers were misleading. Pinned items are populated at runtime
    // from the user's saved views (see useSavedCaseViews).
    sub: [
      { id: "cases-open", label: "Open", path: "/cases/open" },
      {
        id: "cases-evidence-needed",
        label: "Evidence needed",
        path: "/cases/open?view=awaiting_evidence",
      },
      {
        id: "cases-rejected",
        label: "Rejected",
        path: "/cases/open?view=rejected",
        tone: "risk",
      },
      {
        id: "cases-in-review",
        label: "In review",
        path: "/cases/open?view=in_review",
      },
      {
        id: "cases-stale",
        label: "Stale > 7 days",
        path: "/cases/open?view=stale",
      },
      {
        id: "cases-mine",
        label: "Assigned to me",
        path: "/cases/open?view=mine",
      },
      {
        id: "cases-completed",
        label: "Completed",
        path: "/cases/closed",
      },
      {
        id: "cases-exempted",
        label: "Compliance Exempted",
        path: "/cases/exempted",
      },
    ],
  },
  {
    id: "healthcare",
    label: "Rules",
    icon: RulesIcon,
    group: "Compliance",
    // Counts are injected at runtime from live rule data
    // (see useRuleScopeCounts in AppShell). Hardcoded numbers were misleading.
    sub: [
      {
        id: "healthcare-rules-all",
        label: "All rules",
        path: "/healthcare/rules?scope=all",
      },
      {
        id: "healthcare-rules-hcm",
        label: "HCM — Compliance Manual",
        path: "/healthcare/rules?scope=hcm",
      },
      {
        id: "healthcare-rules-svp",
        label: "SVP — Site Visit Protocol",
        path: "/healthcare/rules?scope=svp",
      },
    ],
  },
  {
    id: "documents",
    label: "Documents",
    icon: DocIcon,
    group: "Library",
    path: "/healthcare/files",
  },
  {
    id: "upload",
    label: "Upload",
    icon: UploadIcon,
    group: "Library",
    path: "/healthcare/upload",
  },
  {
    id: "chapter-upload",
    label: "Chapter Checklist Upload",
    icon: ChecklistIcon,
    group: "Library",
    path: "/healthcare/chapter-upload",
  },
  {
    id: "processing-queue",
    label: "Processing Queue",
    icon: QueueIcon,
    group: "Library",
    path: "/healthcare/processing",
  },
  {
    id: "questionnaire-management",
    label: "Questionnaires",
    icon: QuestIcon,
    group: "Assessment",
    sub: [
      {
        id: "questionnaires-list",
        label: "View Questionnaires",
        path: "/questionnaires",
      },
      {
        id: "questionnaires-create",
        label: "Create Questionnaire",
        path: "/questionnaires/new",
      },
      {
        id: "compliance-questionnaire",
        label: "Completed Questionnaire",
        path: "/compliance/questionnaire",
      },
    ],
  },
  {
    id: "needs-assessment",
    label: "Needs Assessment",
    icon: AssessIcon,
    group: "Assessment",
    sub: [
      {
        id: "needs-assessment-template",
        label: "Report Templates",
        path: "/needs-assessment/templates",
      },
      {
        id: "needs-assessment-download",
        label: "Download Assessment",
        path: "/needs-assessment/download",
      },
    ],
  },
  {
    id: "subscriptions",
    label: "Subscriptions",
    icon: SubsIcon,
    group: "Admin",
    sub: [
      {
        id: "subscription-plans",
        label: "Browse Plans",
        path: "/subscriptions/plans",
      },
      {
        id: "subscription-create",
        label: "Create Package",
        path: "/subscriptions/create",
      },
      {
        id: "subscription-manage",
        label: "My Subscriptions",
        path: "/subscriptions/manage",
      },
      {
        id: "subscription-review",
        label: "Review Subscriptions",
        path: "/subscriptions/review",
      },
    ],
  },
  {
    id: "user-management",
    label: "Team",
    icon: TeamIcon,
    group: "Admin",
    sub: [
      { id: "tenants", label: "Tenants", path: "/admin/tenants" },
      { id: "users", label: "Users", path: "/users" },
      { id: "roles", label: "Roles", path: "/roles" },
    ],
  },
  {
    id: "admin",
    label: "System Admin",
    icon: CogIcon,
    group: "Admin",
    sub: [
      {
        id: "admin-document-types",
        label: "Document Types",
        path: "/admin/document-types",
      },
      {
        id: "admin-prompts",
        label: "Extraction Prompts",
        path: "/admin/prompts",
      },
    ],
  },
  {
    id: "account-managers",
    label: "Assign Account Managers",
    icon: AccountManagerIcon,
    group: "Admin",
    path: "/admin/account-managers",
  },
  {
    id: "audit-logs",
    label: "Audit Log",
    icon: AuditIcon,
    group: "Trust",
    path: "/audit-logs",
  },
  {
    id: "cost-explorer",
    label: "API Cost Explorer",
    icon: CostIcon,
    group: "Admin",
    path: "/cost-explorer",
  },
  {
    id: "label-tagging",
    label: "Label Tagging",
    icon: LabelIcon,
    group: "Trust",
    path: "/label-tagging",
  },
  {
    id: "debug",
    label: "API Debug",
    icon: BugIcon,
    group: "Trust",
    path: "/debug",
    devOnly: true,
  },
];

export const AM_RAIL_NAV: RailNavItem[] = [
  {
    id: "am-dashboard",
    label: "Dashboard",
    icon: AMDashIcon,
    group: "AM",
    path: "/account-manager",
  },
  {
    id: "am-customers",
    label: "My Customers",
    icon: AMCustomersIcon,
    group: "AM",
    path: "/account-manager/customers",
  },
  // TODO: wire up Support Tickets page before re-enabling
  // {
  //   id: "am-tickets",
  //   label: "Support Tickets",
  //   icon: AMTicketsIcon,
  //   group: "AM",
  //   path: "/account-manager/tickets",
  // },
  {
    id: "am-compliance",
    label: "Compliance Alerts",
    icon: AMComplianceIcon,
    group: "AM",
    path: "/account-manager/compliance",
  },
  {
    id: "am-assessments",
    label: "Needs Assessments",
    icon: AMAssessmentsIcon,
    group: "AM",
    path: "/account-manager/assessments",
  },
  // TODO: wire up Billing & Revenue page before re-enabling
  // {
  //   id: "am-billing",
  //   label: "Billing & Revenue",
  //   icon: AMBillingIcon,
  //   group: "AM",
  //   path: "/account-manager/billing",
  // },
  {
    id: "am-reports",
    label: "Reports",
    icon: AMReportsIcon,
    group: "AM",
    path: "/account-manager/reports",
  },
  {
    id: "am-settings",
    label: "AM Settings",
    icon: AMSettingsIcon,
    group: "AM",
    path: "/account-manager/settings",
  },
];

/**
 * Returns a flat map of pathname → display label for all known static nav routes.
 * Used by AppShell to record recent navigation history on every route change.
 */
export function buildNavRouteMap(): Map<string, string> {
  const map = new Map<string, string>();
  const stripQuery = (p: string) => p.split(/[?#]/)[0];
  const addItems = (items: RailNavItem[]) => {
    for (const item of items) {
      if (item.path) {
        map.set(item.path, item.label);
      }
      if (item.sub) {
        for (const s of item.sub) {
          map.set(stripQuery(s.path), s.label);
        }
      }
    }
  };
  addItems(RAIL_NAV);
  addItems(AM_RAIL_NAV);
  return map;
}

/** Given a pathname, returns the rail item ID that should be active */
export function getActiveRailId(pathname: string): string {
  // Strip query/hash from sub-item paths when matching against pathname-only input
  const stripQuery = (p: string): string => p.split(/[?#]/)[0];

  // Exact match first
  for (const item of RAIL_NAV) {
    if (item.path && item.path === pathname) return item.id;
    if (item.sub) {
      for (const s of item.sub) {
        if (stripQuery(s.path) === pathname) return item.id;
      }
    }
  }
  // Prefix match (longer prefix wins)
  let best: string | null = null;
  let bestLen = 0;
  for (const item of RAIL_NAV) {
    const paths: string[] = item.path
      ? [item.path]
      : (item.sub ?? []).map((s) => stripQuery(s.path));
    for (const p of paths) {
      if (p !== "/" && pathname.startsWith(p) && p.length > bestLen) {
        best = item.id;
        bestLen = p.length;
      }
    }
  }
  if (best) return best;
  // Default to dashboard for "/"
  return "dashboard";
}

/** Entitlement gate map: rail item ID → whether it should be visible */
export type EntitlementMap = Record<string, boolean>;

export function filterNavItems(
  items: RailNavItem[],
  entitlements: EntitlementMap,
  isAdmin: boolean,
  isDev: boolean,
  childVisibility: Record<string, boolean>,
  _onboardingCompleted: boolean,
): RailNavItem[] {
  return items
    .filter((item) => {
      if (item.devOnly && !isDev) return false;
      if (item.id in entitlements && !entitlements[item.id]) return false;
      return true;
    })
    .map((item) => {
      if (!item.sub) return item;
      const filteredSub = item.sub.filter((s) => childVisibility[s.id] ?? true);
      return { ...item, sub: filteredSub };
    })
    .filter((item) => {
      // Hide parent if all children were filtered out
      if (item.sub && item.sub.length === 0) return false;
      return true;
    });
}
